import { AsyncPipe, DecimalPipe } from '@angular/common';
import type { OnInit, Signal } from '@angular/core';
import {
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  Injector,
  input,
  output,
  signal,
} from '@angular/core';
import {
  takeUntilDestroyed,
  toObservable,
  toSignal,
} from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import type {
  OnyxDropdownOptionsSource,
  OnyxOption,
  OnyxOptionsGroup,
  OnyxSuggestion,
} from '@onyx/angular';
import {
  OnyxCheckboxComponent,
  OnyxDropdownComponent,
  OnyxFormGroupComponent,
  OnyxFormMode,
  OnyxIconBoxComponent,
  OnyxIconButtonComponent,
  OnyxIconComponent,
  OnyxLanguagePipe,
  OnyxModalService,
  OnyxSuggestionsComponent,
  OnyxTabsComponent,
  OnyxTextFieldComponent,
} from '@onyx/angular';
import { chain, difference, isArray, isEqual, isObject, orderBy } from 'lodash';
import type { Observable } from 'rxjs';
import {
  BehaviorSubject,
  combineLatest,
  combineLatestWith,
  delay,
  distinctUntilChanged,
  filter,
  first,
  map,
  shareReplay,
  skip,
  startWith,
  switchMap,
} from 'rxjs';
import { validate as validateUuid } from 'uuid';
import { DictionaryCode } from '../../../../../../common/enums/dictionary-code';
import { GoodsSizeType } from '../../../../../../common/enums/goods-size-type';
import { ValidationHelper } from '../../../../../../common/helpers/validation.helper';
import type { ValidationGroup } from '../../../../../../common/interfaces/validation/validation-group';
import { DictionariesService } from '../../../../../../common/services/dictionaries.service';
import { CompanyDictionaryCode } from '../../../../../management-panel/dictionaries/common/enums/company-dictionary-code';
import type { CompanyGoodType } from '../../../../../management-panel/dictionaries/common/interfaces/company-good-type';
import type { CompanyUnit } from '../../../../../management-panel/dictionaries/common/interfaces/company-unit';
import { CompanyDictionariesService } from '../../../../../management-panel/dictionaries/common/services/company-dictionaries.service';
import type { DictionariesGoodTypeModalData } from '../../../../../management-panel/dictionaries/dictionaries/dictionaries-good-type/dictionaries-good-type-modal/dictionaries-good-type-modal.component';
import { DictionariesGoodTypeModalComponent } from '../../../../../management-panel/dictionaries/dictionaries/dictionaries-good-type/dictionaries-good-type-modal/dictionaries-good-type-modal.component';
import type { DictionariesUnitModalData } from '../../../../../management-panel/dictionaries/dictionaries/dictionaries-units/dictionaries-unit-modal/dictionaries-unit-modal.component';
import { DictionariesUnitModalComponent } from '../../../../../management-panel/dictionaries/dictionaries/dictionaries-units/dictionaries-unit-modal/dictionaries-unit-modal.component';
import type { OrderLoadingPointGoodFormGroup } from '../../../../common/helpers/order-form.helper';
import type { OrderFormGood } from '../../../../common/interfaces/order-form-good';
import { OrderFormService } from '../../../../common/services/order-form.service';
import { UnitSuggestionsPipe } from '../../../../../../common/pipes/unit-suggestions.pipe';
import { UnitWarningPipe } from '../../../../../../common/pipes/unit-warning.pipe';

@Component({
  selector: 'app-order-loading-point-good-form',
  imports: [
    UnitWarningPipe,
    UnitSuggestionsPipe,
    ReactiveFormsModule,
    OnyxIconBoxComponent,
    OnyxIconComponent,
    OnyxDropdownComponent,
    AsyncPipe,
    OnyxTextFieldComponent,
    OnyxSuggestionsComponent,
    OnyxTabsComponent,
    OnyxIconButtonComponent,
    TranslatePipe,
    OnyxFormGroupComponent,
    OnyxCheckboxComponent,
  ],
  templateUrl: './order-loading-point-good-form.component.html',
  styleUrl: './order-loading-point-good-form.component.scss',
})
export class OrderLoadingPointGoodFormComponent implements OnInit {
  private readonly dictionariesService = inject(DictionariesService);
  private readonly injector = inject(Injector);
  private readonly decimalPipe = inject(DecimalPipe);
  private readonly destroyRef = inject(DestroyRef);
  private readonly companyDictionariesService = inject(
    CompanyDictionariesService,
  );
  private readonly languagePipe = inject(OnyxLanguagePipe);
  private readonly modalService = inject(OnyxModalService);
  private readonly orderFormService = inject(OrderFormService);

  public readonly form = input.required<OrderLoadingPointGoodFormGroup>();
  public readonly first = input.required<boolean>();
  public readonly goods = input.required<OrderFormGood[]>();
  public readonly disabled = input(false);
  public readonly parametersValidation = input.required<
    ValidationGroup | undefined
  >();

  public readonly remove = output<void>();

  protected readonly I18N = 'orders.orderForm.points.loadingPoint';

  protected readonly GOOD_TYPE_SOURCE: OnyxDropdownOptionsSource<
    CompanyGoodType | string
  > = {
    list: (query) =>
      this.companyDictionariesService.searchDictionary(
        CompanyDictionaryCode.GOOD_TYPE,
        query,
      ),
    get: (uuid) => {
      if (!validateUuid(uuid)) return { name: uuid, value: uuid };

      return this.companyDictionariesService
        .getDictionary<CompanyDictionaryCode.GOOD_TYPE>(uuid)
        .pipe(
          map((item) => ({
            name: this.languagePipe.transform(item.names),
            value: item,
          })),
        );
    },
    idKey: 'uuid',
  };
  protected readonly QUANTITY_SUGGESTIONS: OnyxSuggestion<number>[] = [33].map(
    (value) => this.buildDecimalSuggestion(value, 0),
  );
  protected readonly DEDICATED_WEIGHT_SUGGESTIONS: OnyxSuggestion<number>[] = [
    24_000,
  ].map((value) => this.buildDecimalSuggestion(value, 0));

  protected readonly GoodsSizeType = GoodsSizeType;

  protected readonly sizeTypes$ = this.dictionariesService.getDictionary(
    DictionaryCode.GOODS_SIZE_TYPE,
  );

  private readonly DEDICATED_LOADING_METERS_SUGGESTIONS: OnyxSuggestion<number>[] =
    [13.6 * 100].map((value) => this.buildDecimalSuggestion(value, 2));

  private readonly goodTypes$ = this.orderFormService.goodTypes$;
  private readonly units$ = this.orderFormService.units$;

  protected adrClasses = this.orderFormService.adrClasses;
  protected isDedicated = this.orderFormService.isDedicated;
  protected hasEcmr = this.orderFormService.hasEcmr;

  protected type$ = new BehaviorSubject<CompanyGoodType | string | null>(null);
  protected unit$ = new BehaviorSubject<CompanyUnit | null>(null);

  protected typeSuggestions$ = this.goodTypes$.pipe(
    map((items): OnyxSuggestion<string>[] =>
      chain(items)
        .filter((item) => ['Neutral'].includes(item.names.en))
        .map((item) => ({
          name: this.languagePipe.transform(item.names),
          value: item.uuid,
        }))
        .value(),
    ),
  );
  protected unitOptions$ = this.units$.pipe(
    combineLatestWith(this.type$),
    map(([units, type]): OnyxOptionsGroup<string, { unit: CompanyUnit }>[] => {
      const relatedUnits = isObject(type) ? type.relatedUnits : [];
      return [
        {
          id: '0.default',
          subheading: `${this.I18N}.relatedUnits`,
          options: relatedUnits.map((unit) => this.buildUnitOption(unit)),
        },
        {
          id: '1.other',
          subheading: relatedUnits.length
            ? `${this.I18N}.otherUnits`
            : undefined,
          options: chain(units)
            .differenceBy(relatedUnits, 'uuid')
            .map((unit) => this.buildUnitOption(unit))
            .value(),
        },
      ].filter((group) => group.options.length);
    }),
    shareReplay(1),
  );
  protected unitSuggestions$ = this.type$.pipe(
    map((type) => (isObject(type) ? type.relatedUnits : [])),
    map((units) => units.map((unit) => this.buildUnitOption(unit)).slice(0, 2)),
  );

  protected loadingMetersSuggestions$!: Observable<OnyxSuggestion<number>[]>;
  protected dimensionsSuggestions$ = this.unit$.pipe(
    map((unit) => {
      const KEYS = ['length', 'width', 'height'] as const;
      return chain(KEYS)
        .map((key) => [
          key,
          unit?.size.type === GoodsSizeType.DIMENSIONS
            ? [this.buildDecimalSuggestion(unit.size[key], 2)]
            : [],
        ])
        .fromPairs()
        .value() as Record<(typeof KEYS)[number], OnyxSuggestion<number>[]>;
    }),
    shareReplay(1),
  );
  protected volumeSuggestions$!: Observable<OnyxSuggestion<number>[]>;

  protected goodTypeQuery = signal('');

  protected goodTypeAddOptionMessage = computed(() =>
    this.goodTypeQuery() ? `${this.I18N}.customGoodType` : 'labels.add',
  );
  protected color = computed(
    () =>
      this.goods().find(({ good }) => good.uuid === this.form().value.uuid)
        ?.goodColor,
  );
  protected showCoilWell = computed(() => {
    const field = this.parametersValidation()?.fields['coilWellLength'];
    return !!field && field.type !== 'forbidden';
  });
  protected sizeType!: Signal<GoodsSizeType | null>;
  protected hasUnitedNationsNumber = computed(() => {
    const adrClasses = this.adrClasses();
    return isArray(adrClasses) && adrClasses.length > 0;
  });
  protected hasStatisticalNumber = computed(() => this.hasEcmr());

  public ngOnInit(): void {
    const controls = this.form().controls;

    this.type$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((type) => isObject(type)),
        filter((type) => type.relatedUnits.length === 1),
        filter(() => controls.unit.value == null),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((type) => {
        const unit = type.relatedUnits[0];

        controls.unit.setValue(unit.uuid);
        this.unit$.next(unit);
      });

    this.unitOptions$
      .pipe(
        first(),
        delay(0),
        switchMap(() => this.unit$),
        skip(1),
        distinctUntilChanged((previous, current) => isEqual(previous, current)),
        filter((unit) => !!unit),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((unit) => {
        if (!this.showCoilWell()) unit.size.requiresCoilWell = null;

        this.form().patchValue({ size: unit.size });
      });

    const quantity$ = controls.quantity.valueChanges.pipe(
      startWith(controls.quantity.value),
    );
    this.loadingMetersSuggestions$ = combineLatest([
      toObservable(this.isDedicated, { injector: this.injector }),
      this.unit$,
      quantity$,
    ]).pipe(
      map(([isDedicated, unit, quantity]) => [
        ...(isDedicated ? this.DEDICATED_LOADING_METERS_SUGGESTIONS : []),
        ...(unit?.size.type === GoodsSizeType.LOADING_METERS
          ? [
              this.buildDecimalSuggestion(unit.size.loadingMeters, 2),
              ...(quantity != null && quantity > 1
                ? [
                    this.buildDecimalSuggestion(
                      unit.size.loadingMeters * quantity,
                      2,
                    ),
                  ]
                : []),
            ]
          : []),
      ]),
      map((suggestions) =>
        orderBy(suggestions, (suggestion) => suggestion.value),
      ),
      shareReplay(1),
      takeUntilDestroyed(this.destroyRef),
    );
    this.volumeSuggestions$ = combineLatest([this.unit$, quantity$]).pipe(
      map(([unit, quantity]) =>
        unit?.size.type === GoodsSizeType.VOLUME
          ? [
              this.buildDecimalSuggestion(unit.size.volume, 3),
              ...(quantity != null && quantity > 1
                ? [this.buildDecimalSuggestion(unit.size.volume * quantity, 3)]
                : []),
            ]
          : [],
      ),
      shareReplay(1),
      takeUntilDestroyed(this.destroyRef),
    );

    const sizeControls = {
      ...controls.size.controls,
      quantity: controls.quantity,
    };

    this.sizeType = toSignal(sizeControls.type.valueChanges, {
      initialValue: sizeControls.type.value,
      injector: this.injector,
    });

    const SIZE_TYPE_CONTROLS = {
      [GoodsSizeType.DIMENSIONS]: [
        sizeControls.length,
        sizeControls.width,
        sizeControls.height,
        sizeControls.quantity,
      ],
      [GoodsSizeType.LOADING_METERS]: [
        sizeControls.loadingMeters,
        sizeControls.requiresCoilWell,
      ],
      [GoodsSizeType.VOLUME]: [sizeControls.volume],
    };
    const sizeChanges = toSignal(
      controls.size.valueChanges.pipe(distinctUntilChanged(isEqual)),
      { injector: this.injector },
    );

    effect(
      () => {
        sizeChanges();

        const type = this.sizeType();
        const enabledSizeTypeControls = type ? SIZE_TYPE_CONTROLS[type] : [];
        const enabledControls = [sizeControls.type, ...enabledSizeTypeControls];
        const disabledControls = difference(
          Object.values(sizeControls),
          enabledControls,
        );

        const isDedicated = this.isDedicated();
        ValidationHelper.toggleRequiredValidator(
          sizeControls.type,
          !isDedicated,
        );

        ValidationHelper.enableControls(enabledControls);
        ValidationHelper.disableControls(disabledControls, { reset: true });
      },
      { injector: this.injector },
    );

    effect(
      () => {
        const hasCoilWell = this.showCoilWell();
        ValidationHelper.toggleControls(sizeControls.requiresCoilWell, {
          enabled: hasCoilWell,
          reset: !hasCoilWell,
        });

        if (
          hasCoilWell &&
          this.sizeType() === GoodsSizeType.LOADING_METERS &&
          sizeControls.requiresCoilWell.getRawValue() == null
        ) {
          sizeControls.requiresCoilWell.setValue(false);
        }
      },
      { injector: this.injector },
    );

    controls.unitedNationsNumber.valueChanges
      .pipe(distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe((value) =>
        controls.unitedNationsNumber.setValue(
          value?.replace(/\D/g, '') ?? null,
          { emitEvent: false },
        ),
      );

    effect(
      () => {
        ValidationHelper.toggleControls(
          this.form().controls.unitedNationsNumber,
          { enabled: this.hasUnitedNationsNumber(), reset: true },
        );
        ValidationHelper.toggleControls(
          this.form().controls.statisticalNumber,
          { enabled: this.hasStatisticalNumber(), reset: true },
        );
      },
      { injector: this.injector },
    );
  }

  protected setCustomType(type: string): void {
    this.form().controls.type.setValue(type);
  }

  protected addGoodType(name?: string): void {
    if (name) {
      this.setCustomType(name);
      return;
    }

    this.modalService.open<DictionariesGoodTypeModalData>(
      DictionariesGoodTypeModalComponent,
      { mode: OnyxFormMode.ADD },
    );
  }

  protected addUnit(name?: string): void {
    this.modalService.open<DictionariesUnitModalData>(
      DictionariesUnitModalComponent,
      { mode: OnyxFormMode.ADD, name },
    );
  }

  private buildUnitOption(
    unit: CompanyUnit,
  ): OnyxOption<string> & { unit: CompanyUnit } {
    return {
      name: this.languagePipe.transform(unit.names),
      value: unit.uuid,
      unit,
    };
  }

  private buildDecimalSuggestion(
    value: number,
    decimalPlaces: number,
  ): OnyxSuggestion<number> {
    return {
      name: this.decimalPipe.transform(value / 10 ** decimalPlaces)!,
      value: value,
    };
  }
}
